import { useState } from 'react';
import { CheckCircle, Warning, Eye, EyeSlash } from '@phosphor-icons/react';

import {
  passwordStrength,
  passwordRequirements,
  passwordsMatch,
  type PasswordFormValue,
} from '@/lib/passwordPolicy';

/**
 * The password fields — current / new / confirm, with the reveal toggle, strength meter, live
 * requirements and match warning.
 *
 * ONE implementation, used by Settings → Security and by the forced first-sign-in change. It is
 * deliberately controlled and submit-less: the parent owns the value, the button and the save
 * state, because those differ (a settings panel saves inline; the forced modal cannot be
 * dismissed). What must NOT differ — the rules, the meter, the wording — lives here.
 */
export function PasswordChangeFields({
  value,
  onChange,
  requireCurrent = true,
  currentLabel = 'Current password',
  disabled = false,
}: {
  value: PasswordFormValue;
  onChange: (next: PasswordFormValue) => void;
  /** False only where identity was already proven another way (e.g. a reset link). */
  requireCurrent?: boolean;
  /** Overridden by the forced-change modal, where "current" is the temporary password. */
  currentLabel?: string;
  disabled?: boolean;
}) {
  const [reveal, setReveal] = useState(false);

  const score = passwordStrength(value.next);
  const matches = passwordsMatch(value);
  const requirements = passwordRequirements(value.next);
  const reusesCurrent = requireCurrent && !!value.next && value.current === value.next;

  const set = (patch: Partial<PasswordFormValue>) => onChange({ ...value, ...patch });

  const inputClass =
    'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground ' +
    'outline-none transition focus:border-brand-500 disabled:opacity-60';

  return (
    <div className="space-y-3">
      {requireCurrent && (
        <div>
          <label className="text-xs font-medium text-muted-foreground">{currentLabel}</label>
          <div className="relative mt-1">
            <input
              value={value.current}
              onChange={(e) => set({ current: e.target.value })}
              type={reveal ? 'text' : 'password'}
              autoComplete="current-password"
              disabled={disabled}
              className={inputClass}
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setReveal((r) => !r)}
              aria-label={reveal ? 'Hide passwords' : 'Show passwords'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/60 hover:text-muted-foreground"
            >
              {reveal ? <EyeSlash size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>
      )}

      <div>
        <label className="text-xs font-medium text-muted-foreground">New password</label>
        <input
          value={value.next}
          onChange={(e) => set({ next: e.target.value })}
          type={reveal ? 'text' : 'password'}
          autoComplete="new-password"
          disabled={disabled}
          className={`${inputClass} mt-1`}
        />
        {value.next && (
          <div className="mt-2 flex gap-1">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= score
                    ? ['', 'bg-red-500', 'bg-amber-500', 'bg-blue-500', 'bg-emerald-500'][score]
                    : 'bg-muted'
                }`}
              />
            ))}
          </div>
        )}
        {reusesCurrent && (
          <p className="mt-1 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
            <Warning size={11} weight="fill" /> Choose something different from your current password.
          </p>
        )}
      </div>

      <div>
        <label className="text-xs font-medium text-muted-foreground">Confirm new password</label>
        <input
          value={value.confirm}
          onChange={(e) => set({ confirm: e.target.value })}
          type={reveal ? 'text' : 'password'}
          autoComplete="new-password"
          disabled={disabled}
          className={`${inputClass} mt-1 ${matches ? '' : 'border-red-500'}`}
        />
        {!matches && (
          <p className="mt-1 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
            <Warning size={11} weight="fill" /> These do not match.
          </p>
        )}
      </div>

      {value.next && (
        <ul className="rounded-lg border border-border bg-muted/30 p-3">
          {requirements.map((r) => (
            <li key={r.label} className="flex items-center gap-2 py-0.5 text-xs">
              <CheckCircle
                size={12}
                weight={r.met ? 'fill' : 'regular'}
                className={r.met ? 'text-emerald-500' : 'text-muted-foreground/30'}
              />
              <span className={r.met ? 'text-foreground' : 'text-muted-foreground'}>{r.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
